import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Position } from "./types";

/**
 * スクロール位置の計算関数
 */
function calculatePosition(props: {
  scrollWidth: number;
  clientWidth: number;
  scrollLeft: number;
}): Position | undefined {
  const { scrollWidth, clientWidth, scrollLeft } = props;

  if (scrollWidth <= clientWidth) return undefined;
  else if (scrollLeft === 0) return "left";
  else if (scrollWidth - scrollLeft - clientWidth < 1) return "right";
  else return "middle";
}

/**
 * スクロール可能な要素のナビゲーション機能を提供するカスタムフック
 *
 * @returns スクロール状態と制御オブジェクト
 */
export function useScrollableNavigation() {
  // 現在のスクロール位置状態
  const [position, setPosition] = useState<Position | undefined>(undefined);

  // コンテナとリサイズオブザーバーの参照
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  // アンマウント状態を追跡する参照
  const isMountedRef = useRef(true);

  /**
   * スクロール位置の更新処理
   */
  const updatePosition = useCallback(() => {
    // コンポーネントがアンマウントされている場合は更新しない
    if (!isMountedRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;

    const newPosition = calculatePosition({
      scrollWidth,
      clientWidth,
      scrollLeft,
    });

    setPosition(newPosition);
  }, []);

  const handleScroll = useDebouncedCallback(updatePosition, [], 50);

  // サイズ変更の監視とイベントリスナーの設定
  useEffect(() => {
    isMountedRef.current = true;

    // ResizeObserverを使用して要素のサイズ変更を監視
    observerRef.current = new ResizeObserver(() => {
      if (isMountedRef.current) {
        updatePosition();
      }
    });

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observerRef.current.observe(currentContainer);

      // 初期表示時にもポジションを更新
      requestAnimationFrame(() => {
        if (isMountedRef.current) {
          updatePosition();
        }
      });
    }

    // リサイズイベントのリスナーを追加
    const handleResize = () => {
      if (isMountedRef.current) {
        updatePosition();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      isMountedRef.current = false;

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      window.removeEventListener("resize", handleResize);
    };
  }, [updatePosition]);

  /**
   * 前の要素にスクロール
   */
  const scrollToPrevious = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.scrollTo({
      left: container.scrollLeft - container.clientWidth,
      behavior: "smooth",
    });
  }, []);

  /**
   * 次の要素にスクロール
   */
  const scrollToNext = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    container.scrollTo({
      left: container.scrollLeft + container.clientWidth,
      behavior: "smooth",
    });
  }, []);

  return {
    containerRef,
    position,
    handleScroll,
    scrollToPrevious,
    scrollToNext,
    isAtStart: position === undefined || position === "left",
    isAtEnd: position === undefined || position === "right",
  };
}
